// AddAgentDialog.jsx
import React, { useState, useEffect, useRef } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import Checkbox from "aldoo-ra/Checkbox"
import Typography from "aldoo-ra/Typography"
import { showNotification } from "aldoo-ra/Notification"
import { showMessageDialog } from "aldoo-ra/MessageDialog"
import { AddAgent, EditAgent } from "./api"
import ContentEditor from "aldoo-ra/CMS/content-editor/index"
import Controller from "aldoo-ra/CMS/controllers/cms-controller"
import TypeResolver from "aldoo-ra/CMS/type-resolver"

const COMMON_PERMISSIONS = [
  { value: "tickets:read", label: "View Tickets" },
  { value: "tickets:write", label: "Edit Tickets" },
  { value: "tickets:delete", label: "Delete Tickets" },
  { value: "tickets:assign", label: "Assign Tickets" },
  { value: "users:read", label: "View Users" },
  { value: "admin:access", label: "Admin Access" },
]

export default function AddAgentDialog({
  isOpen,
  onClose,
  onAgentAdded,
  agent = null,
}) {
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [permissions, setPermissions] = useState([])
  const [isAgent, setIsAgent] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState("")
  const [touched, setTouched] = useState({ email: false, name: false })
  const [showSignatureEditor, setShowSignatureEditor] = useState(false)
  const [signatureContent, setSignatureContent] = useState(null)
  const [isLoadingSignature, setIsLoadingSignature] = useState(false)
  const isInternalSignatureChange = useRef(false)

  const controller = Controller()
  const { getContentType } = TypeResolver()

  // Initialize form when editing an existing agent
  useEffect(() => {
    if (agent) {
      setName(agent.name || "")
      setEmail(agent.email || "")
      setIsAgent(agent.isAgent !== false)
      setPermissions(agent.permissions || [])

      // Load signature if editing an existing agent
      if (agent.email) {
        loadAgentSignature(agent.email)
      }
    } else {
      // Reset form for a new agent
      setName("")
      setEmail("")
      setIsAgent(true)
      setPermissions([])
      setSignatureContent(null)
      setTouched({ email: false, name: false })
    }
  }, [agent, isOpen])

  const handlePermissionChange = (permission) => {
    setPermissions((prev) => {
      if (prev.includes(permission)) {
        return prev.filter((p) => p !== permission)
      } else {
        return [...prev, permission]
      }
    })
  }

  // Load the agent's signature from CMS
  const loadAgentSignature = async (agentEmail) => {
    if (!agentEmail) return

    setIsLoadingSignature(true)
    try {
      const result = await controller.getContent({
        match: {
          "content.type": "CSAgentSignature",
          "content.agent": agentEmail,
          deleted: { $ne: true },
        },
      })

      if (result?.data && result.data.length > 0) {
        isInternalSignatureChange.current = true
        setSignatureContent(result.data[0])
      } else {
        // No signature found, create a default one
        isInternalSignatureChange.current = true
        setSignatureContent({
          content: {
            type: "CSAgentSignature",
            agent: agentEmail,
            html: "",
          },
        })
      }
    } catch (error) {
      console.error("Failed to load agent signature:", error)
      showNotification({
        message: "Failed to load agent signature",
        className: "bg-admin_error text-white",
      })
    } finally {
      setIsLoadingSignature(false)
    }
  }

  // Handle signature save
  const handleSignatureSave = async (content) => {
    try {
      // Format the content according to the CMS controller expectations
      const payload = {
        ...(signatureContent._id ? { _id: signatureContent._id } : {}),
        content: {
          ...content,
          type: "CSAgentSignature",
          agent: email,
        },
      }

      const response = await controller.saveContent({
        payload,
        autoShowNotifications: true,
      })

      if (response.error) {
        return null
      }

      // If successful, fetch the updated content
      if (response.result) {
        const result = await controller.getContent({
          match: { _id: response.result },
        })

        if (result?.data && result.data.length > 0) {
          isInternalSignatureChange.current = true
          setSignatureContent(result.data[0])
          return result.data[0]
        }
      }

      return null
    } catch (error) {
      console.error("Failed to save signature:", error)
      showNotification({
        message: "Failed to save signature",
        className: "bg-admin_error text-white",
      })
      return null
    }
  }

  const handleSubmit = async () => {
    // Mark all fields as touched to show validation errors
    setTouched({ email: true, name: true })

    // Validation - email and name are required
    if (!email.trim()) {
      setError("Email is required")
      return
    }

    if (!name.trim()) {
      setError("Name is required for new user")
      return
    }

    setError("")
    setIsSubmitting(true)

    try {
      // Determine which permissions to add and which to revoke
      let result
      if (agent) {
        // For existing agent, figure out which permissions to add/remove
        const existingPermissions = agent.permissions || []
        const newPermissions = permissions.filter(
          (p) => !existingPermissions.includes(p)
        )
        const revokePermissions = existingPermissions.filter(
          (p) => !permissions.includes(p)
        )

        result = await EditAgent({
          userId: agent.id,
          name,
          email,
          isAgent,
          permissions: newPermissions.length ? newPermissions : undefined,
          revokePermissions: revokePermissions.length
            ? revokePermissions
            : undefined,
        })
      } else {
        // For new agent
        result = await AddAgent({
          name,
          email,
        })

        // If successful and permissions needed, add permissions
        if (result.result && permissions.length > 0) {
          await EditAgent({
            userId: result._id,
            permissions,
          })
        }
      }

      if (result.error) {
        setError(result.error)
        showNotification({
          message: `Failed: ${result.error}`,
          className: "bg-admin_error text-white",
        })
      } else {
        showNotification({
          message: agent
            ? "Agent updated successfully"
            : "Agent added successfully",
          className: "bg-admin_primary text-admin_text_inv",
        })

        // Add message dialog for new agent
        if (!agent) {
          showMessageDialog({
            title: "Agent Invitation Sent",
            message: `An email has been sent to ${email} with instructions on how to join the platform.`,
            variant: "success",
          })
        }

        if (onAgentAdded) onAgentAdded()
        onClose()
      }
    } catch (error) {
      setError("An unexpected error occurred")
      showNotification({
        message: "Failed to process request",
        className: "bg-admin_error text-white",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={agent ? "Edit Support Agent" : "Add Support Agent"}
      variant="admin_centered"
    >
      <div className="p-6 space-y-6">
        {error && error !== "Email is required" && error !== "Name is required for new user" && (
          <div className="p-2 mb-4 border border-admin_error text-admin_error rounded">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <Input
            variant="admin"
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched({ ...touched, email: true })}
            placeholder="Enter agent's email"
            required
            error={touched.email && !email.trim() && error === "Email is required" ? "Email is required" : ""}
            readOnly={agent !== null} // Can't change email for existing agents
            autoFocus={!agent}
            styleOverrides={{
              label: "text-base font-bold text-admin_text mb-2",
            }}
          />

          <Input
            variant="admin"
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setTouched({ ...touched, name: true })}
            placeholder="Enter agent's name"
            required
            error={touched.name && !name.trim() && error === "Name is required for new user" ? "Name is required" : ""}
            styleOverrides={{
              label: "text-base font-bold text-admin_text mb-2",
            }}
          />

          {agent && (
            <div className="mt-4">
              <Typography variant="admin_body_accent" className="mb-2">
                Agent Status
              </Typography>

              <Checkbox
                variant="admin"
                label="Active Support Agent"
                isOn={isAgent}
                onChange={() => setIsAgent(!isAgent)}
              />
            </div>
          )}

          {/* New Email Signature section */}
          {agent && (
            <div className="mt-4">
              <Typography variant="admin_body_accent" className="mb-2">
                Email Signature
              </Typography>

              <Button
                variant="admin_secondary"
                onClick={() => {
                  if (email) {
                    if (!signatureContent) {
                      loadAgentSignature(email)
                    }
                    // Check if the content type exists before opening the editor
                    if (getContentType("CSAgentSignature")) {
                      setShowSignatureEditor(true)
                    } else {
                      showNotification({
                        message:
                          "Email signature type is not defined in the CMS",
                        className: "bg-admin_error text-white",
                      })
                    }
                  } else {
                    showNotification({
                      message: "Please provide an email address first",
                      className: "bg-admin_error text-white",
                    })
                  }
                }}
                disabled={isLoadingSignature}
                className="w-auto"
              >
                {isLoadingSignature ? "Loading..." : "Edit Email Signature"}
              </Button>

              {signatureContent?.content?.html && (
                <div className="mt-2 text-sm text-admin_text_sec">
                  Signature is set (
                  {signatureContent.content.html.length > 50
                    ? `${signatureContent.content.html.substring(0, 50)}...`
                    : signatureContent.content.html}
                  )
                </div>
              )}
            </div>
          )}

          <div className="mt-6">
            <Typography variant="admin_body_accent" className="mb-1">
              Permissions (optional)
            </Typography>

            <div className="p-4 border border-admin_bordered rounded-md space-y-2 max-h-60 overflow-y-auto">
              {COMMON_PERMISSIONS.map((permission) => (
                <div key={permission.value} className="flex items-center">
                  <Checkbox
                    variant="admin"
                    label={permission.label}
                    isOn={permissions.includes(permission.value)}
                    onChange={() => handlePermissionChange(permission.value)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-4 mt-8">
          <Button
            variant="admin_secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>

          <Button
            variant="admin_primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Processing..."
              : agent
              ? "Update Agent"
              : "Add Agent"}
          </Button>
        </div>
      </div>

      {/* Signature Editor Modal */}
      <Modal
        isOpen={showSignatureEditor}
        onClose={() => setShowSignatureEditor(false)}
        title="Edit Email Signature"
        variant="admin"
        className="w-full sm:w-[500px] md:w-[85%] h-full md:h-auto z-200"
      >
        {showSignatureEditor && signatureContent && (
          <ContentEditor
            title="Email Signature"
            allowContentImport={false}
            contentType={getContentType("CSAgentSignature")}
            dataItem={signatureContent}
            fieldValues={{
              ...signatureContent?.content,
              _id: signatureContent?._id,
            }}
            onError={(errors) => {
              showNotification({
                message: Object.values(errors).find((e) => e),
                className: "bg-admin_error text-white",
              })
            }}
            onSave={handleSignatureSave}
            onClickSave={() => setShowSignatureEditor(false)}
          />
        )}
      </Modal>
    </Modal>
  )
}
